// 必要なライブラリやコンポーネントをインポート
import { useSearchParams } from 'react-router-dom'; // ルートのクエリパラメータを操作するためのフック
import UserFrame from "../organisms/UserFrame"; // 全体レイアウトを提供するフレームコンポーネント
import { 
    Container, Box, Grid, AppBar, Typography, Checkbox, Radio, Button, Stack, Modal, List, 
    ListItem, IconButton, ListItemButton, ListItemIcon, ListItemText, Chip, Divider, 
    ToggleButtonGroup, ToggleButton, Accordion, AccordionSummary, AccordionDetails, 
    LinearProgress, Collapse, 
    Fab,
    Tooltip
} from '@mui/material'; // Material-UIのレイアウトコンポーネントやUI要素
import { useEffect, useLayoutEffect, useRef, useState } from 'react'; // Reactのライフサイクルフックとステート管理
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // アコーディオンの展開アイコン
import ErrorIcon from '@mui/icons-material/Error'; // エラー表示用アイコン
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'; // ラジオボタン（未選択）アイコン
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'; // ラジオボタン（選択済み）アイコン
import CancelIcon from '@mui/icons-material/Cancel'; // キャンセルアイコン
import AccessTimeIcon from '@mui/icons-material/AccessTime'; // 時計アイコン（時間表示用）
import DoughnutChart from '../atoms/Doughnut'; // ドーナツ型グラフ表示用コンポーネント
import axios from 'axios'; // HTTPリクエスト用ライブラリ
import ExcelJS from "exceljs"; // Excelファイル生成用ライブラリ
import { useRecoilValue } from 'recoil'; // Recoil（グローバルステート管理）の値を取得するフック
import userState from '../../recoil/atoms/userState'; // ユーザー情報を管理するRecoilのアトム
import TextAndImg from '../molecules/TextAndImg'; // テキストと画像を表示するためのコンポーネント
import ChevronRightIcon from '@mui/icons-material/ChevronRight'; // 右向き矢印アイコン（サイドバー操作用）
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'; // 左向き矢印アイコン（サイドバー操作用）
import React, { Component } from "react";
import { usePdfExport } from '../hooks/usePdfExport';
import Certificate1PDF from '../atoms/Certificate1PDF';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

// 定数
const allowed = [8, 9, 10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31];

// モーダル用スタイルオブジェクトの定義
const style = {
    position: 'absolute' as 'absolute', // 絶対位置に配置
    top: '50%', // 縦方向中央揃え
    left: '50%', // 横方向中央揃え
    transform: 'translate(-50%, -50%)', // 中央揃えのために移動
    width: 500, // モーダルの幅
    bgcolor: 'background.paper', // 背景色を設定
    border: '1px solid #aaa', // 境界線
    boxShadow: 24, // シャドウ効果
    p: 4, // パディング
};

// プロパティの型定義
type Props = {
    practiceList: PracticeType[][], // 複数の練習問題リスト
    tests: TestType[], // テスト問題リスト
    questionBook: number, // 現在選択されている問題集ID
    userId: number, // ユーザーID
    saveData?: SaveDataType, //中断データ
    isCompleted?: boolean;
    setIsCompleted?: (v: boolean) => void;
    onTestComplete?: () => void
};

// 練習問題の型定義
export type PracticeType = {
    practiceId: number, // 練習問題ID
    practiceName: string, // 練習問題名
    triedCount: number // 試行回数
    topic1: string, // トピック1
    topic2: string, // トピック2
    topic3: string, // トピック3
    topic4: string, // トピック4
    topic5: string, // トピック5
    topic6: string, // トピック6
    topic7: string, // トピック7
    topic8: string, // トピック8
    topic9: string, // トピック9
    topic10: string, // トピック10
    topic11: string, // トピック11
    topic12: string, // トピック12
    topic13: string, // トピック13
    topic14: string, // トピック14
};

// 選択肢の型定義
export type ChoiceType = {
    choiceNum: number, // 選択肢番号
    choiceText: string, // 選択肢のテキスト
    isCorrect: boolean // 正解かどうか
};

// テスト問題の型定義
export type TestType = {
    testId: number, // テスト問題ID
    question: string, // 問題文
    choices: ChoiceType[], // 選択肢リスト
    answer: number[], // 正解の選択肢番号リスト
    explanation: string, // 解説文
    images?: ImageType[], // 解説用の画像データ（任意）
};

// 画像データの型定義
export type ImageType = {
    path: string, // 画像のパス
    data: string, // 画像データ
};

// 中断データの型定義
export type SaveDataType = {
    questionBook: number, // 問題集ID
    questionNums: number, // 中断No,
    choiceNums: (string | null)[], // 回答選択肢
    questionOrder: number[], // 並び順
    elapsed: string, // 経過時間
};

// 回答一時保存用型定義
type TmpAnswerType = {
    id: number,
    answer: string | null,
}

// 問題のカテゴリリスト
const problemCategory = [
    "初級コース", "中級コース", "上級コース", "AWS SAA"
];

/** ユーザー問題解答用のテンプレート */
const UsersProblemTemplate = (props: Props) => {
    const initNo = useRef<number>(100);

    //テスト完了ボタンが押されたかを管理するフラグ
    const [isCompleted, setIsCompleted] = useState(false);
    const isCompletedRef = useRef(false);

    // propsからデータを取得
    const {practiceList, userId, saveData} = props;
    const [tests , setTests] = useState<TestType[]>([...props.tests]);
    const [images, setImages] = useState<TestType[]>([...props.tests]);
    const [questionBook , setQuestionBook] = useState<number>(props.questionBook);
    useEffect(()=>{
        setQuestionBook(props.questionBook);
    }, [props.questionBook])

    const userData = useRecoilValue(userState); // Recoilのユーザー情報を取得

    // 現在選択されている練習問題リストを取得
    let practices: PracticeType[] = [];
    practiceList?.forEach(list => {
        const pr = list.find(p => p.practiceId === questionBook);
        if (pr) {
            practices = [...list];
        }
    });

    // 現在の練習問題を特定
    const practice = practices.find(p => p.practiceId === questionBook);

    // 次の問題IDを計算
    let TmpNextId: null | number = null;
    let curFlg = false;
    practices?.forEach((p, idx) => {
        if (idx + 1 < practices?.length && curFlg) {
            TmpNextId = p.practiceId; // 次の問題IDを設定
            curFlg = false;
        } else if (p.practiceId === practice?.practiceId) {
            curFlg = true; // 現在の問題IDが一致した場合に次を設定可能
        }
    });
    const [nextId, setNextId] = useState(TmpNextId);
    // 時間管理関連のステートを初期化
    const now = new Date();
    const [executedAt, setExecutedAt] = useState("0000-00-00");
    const [startTime, setStartTime] = useState(now.getTime()); // 開始時間
    const testTime = 2 * 60 * 60 * 1000 + 10 * 60 * 1000; // テスト時間（2時間10分）
    const [endTime, setEndTime] = useState(0); // 終了時間
    const [phase, setPhase] = useState<'test' | 'result' | 'exp'>('test'); // 現在のフェーズ
    const [side, setSide] = useState<'practices' | 'tests'>('practices'); // 表示するサイドメニュー
    const [tryingNo, setTryingNo] = useState(0); // 現在の問題番号
    const [selectedCoice, setSelectedChoice] = useState<number[]>([]); // 選択された回答番号リスト
    // トピック開閉フラグ
    const [isParentClicked, setIsParentClicked] = useState(false); // 初期状態は非活性
    // 再テストボタン押下フラグ
    const [isRepTestFlg, setIsRepTestFlg] = useState(false);
    const [completedQuestionBooks, setCompletedQuestionBooks] = useState<number[]>([]);
    const [completedBooks, setCompletedBooks] = useState<number[]>([]);
    const initCount = useRef(0);

    // テストを完了時にリストにquestionBookを格納
    useEffect(() => {
        if (phase === 'result') {
            if (!completedQuestionBooks.includes(questionBook)) {
                setCompletedQuestionBooks(prev => {
                    const updated = [...prev, questionBook];
                    return updated;
                });
            }
        }
    }, [phase]);

    // 合格割合
    const passRatio = [0.72,0.72,0.72,0.72];

    // ユーザー回答の初期化
    const ua: number[][] = [];
    for (let i = 0; i < tests?.length; i++) {
        ua.push([]); // 各問題ごとに空の配列を用意
    }
    
    // ユーザーの回答状況を管理するステート
    const [userAnswers, setUserAnswers] = useState<number[][]>(ua); // 各問題に対する回答
    useEffect(() => {
        if(saveData && props.tests?.length > 0) {
            // 中断データがある場合は回答を格納
            const tmpAnswers: number[][] = [];
            setQuestionBook(props.questionBook);
            setTopic1Selected(true);
            setIsParentClicked(true);
            initNo.current = 0;

            for (let i = 0; i < saveData.choiceNums?.length; i++) {
                const tmpAnswer: number[] = [];
                saveData.choiceNums[i]?.split(',')?.forEach(c => {
                    tmpAnswer.push(props.tests[i]?.choices?.findIndex(tc => tc?.choiceNum === Number(c)));
                })
                tmpAnswers.push(tmpAnswer); // 各問題ごとに空の配列を用意
            }
            setUserAnswers(tmpAnswers);
            initNo.current = saveData.questionNums;
            setTryingNo(saveData.questionNums);
            const hms = saveData.elapsed?.split(':');
            const nowR = new Date();
            if(!allowed.includes(questionBook)){
                setStartTime(startTime - Number(hms[0]) * 3600000 - Number(hms[1]) * 60000 - Number(hms[2]) * 1000);                
            }
            console.log(props.questionBook, saveData.questionNums, saveData.choiceNums);
        }
    }, [saveData])
    const [score, setScore] = useState(0); // 現在のスコア
    const [results, setResults] = useState<boolean[]>([]); // 各問題の正解・不正解状況
    const [isSent, setIsSent] = useState(false); // 回答が送信済みかどうか
    const [isAnswered, setIsAnswered] = useState(false); // 実施済みかどうか
    const [click, setClick] = useState(0);
    const [firstFiveAnswered, setFirstFiveAnswered] = useState(false);
    const [nextFiveAnswered, setNextFiveAnswered] = useState(false);
    const [thirdFiveAnswered, setThirdFiveAnswered] = useState(false);
    const [fourthFiveAnswered, setFourthFiveAnswered] = useState(false);
    const [fifthFiveAnswered, setFifthFiveAnswered] = useState(false);
    const [sixthFiveAnswered, setSixthFiveAnswered] = useState(false);
    const [seventhFiveAnswered, setSeventhFiveAnswered   ] = useState(false);
    const [eighthFiveAnswered, setEighthFiveAnswered] = useState(false);
    const [ninthFiveAnswered, setNinthFiveAnswered] = useState(false);
    const [tenthFiveAnswered, setTenthFiveAnswered] = useState(false);
    const [eleventhFiveAnswered, setEleventhFiveAnswered] = useState(false);
    const [twelfthFiveAnswered, setTwelfthFiveAnswered] = useState(false);
    const [thirteenthFiveAnswered, setThirteenthFiveAnswered] = useState(false);
    const [fourteenthFiveAnswered, setFourteenthFiveAnswered] = useState(false);
    
        // 初回マウント時にテスト完了ボタンをfalseにリセット
        useEffect(() => {
            setIsCompleted(false);
        }, []);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 5) { 
            const isFirstFiveComplete = userAnswers.slice(0, 5).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setFirstFiveAnswered(isFirstFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 10) { 
            const isNextFiveComplete = userAnswers.slice(5, 10).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setNextFiveAnswered(isNextFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 15) {
            const isThirdFiveComplete = userAnswers.slice(10, 15).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setThirdFiveAnswered(isThirdFiveComplete);
        }
    }, [userAnswers]);

    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 20) {
            const isFourthFiveComplete = userAnswers.slice(15, 20).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setFourthFiveAnswered(isFourthFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 25) {
            const isFifthFiveComplete = userAnswers.slice(20, 25).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setFifthFiveAnswered(isFifthFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 30) {
            const isSixthFiveComplete = userAnswers.slice(25, 30).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setSixthFiveAnswered(isSixthFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 35) {
            const isSeventhFiveComplete = userAnswers.slice(30, 35).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setSeventhFiveAnswered(isSeventhFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 40) {
            const isEighthFiveComplete = userAnswers.slice(35, 40).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setEighthFiveAnswered(isEighthFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 45) {
            const isNinthFiveComplete = userAnswers.slice(40, 45).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setNinthFiveAnswered(isNinthFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 50) {
            const isTenthFiveComplete = userAnswers.slice(45, 50).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setTenthFiveAnswered(isTenthFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 55) {
            const isEleventhFiveComplete = userAnswers.slice(50, 55).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setEleventhFiveAnswered(isEleventhFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 60) {
            const isTwelfthFiveComplete = userAnswers.slice(55, 60).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setTwelfthFiveAnswered(isTwelfthFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 65) {
            const isThirteenthFiveComplete = userAnswers.slice(60, 65).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setThirteenthFiveAnswered(isThirteenthFiveComplete);
        }
    }, [userAnswers]);
    useEffect(() => {
        if (tryingCategory !== 3 && userAnswers.length >= 70) {
            const isFourteenthFiveComplete = userAnswers.slice(65, 70).every(answer => answer !== undefined && answer !== null && answer.length > 0);
            setFourteenthFiveAnswered(isFourteenthFiveComplete);
        }
    }, [userAnswers]);

    // モーダル関連のステート
    const [open, setOpen] = useState(false); // モーダルの開閉状態
    const handleClose = () => setOpen(false); // モーダルを閉じる関数
    const [isTopicOpen, setIsTopicOpen] = useState(false); // トピック選択モーダルの開閉状態
    const [topic1Selected, setTopic1Selected] = useState(false);
    const [topic2Selected, setTopic2Selected] = useState(false);
    const [topic3Selected, setTopic3Selected] = useState(false);
    const [topic4Selected, setTopic4Selected] = useState(false);
    const [topic5Selected, setTopic5Selected] = useState(false);
    const [topic6Selected, setTopic6Selected] = useState(false);
    const [topic7Selected, setTopic7Selected] = useState(false);
    const [topic8Selected, setTopic8Selected] = useState(false);
    const [topic9Selected, setTopic9Selected] = useState(false);
    const [topic10Selected, setTopic10Selected] = useState(false);
    const [topic11Selected, setTopic11Selected] = useState(false);
    const [topic12Selected, setTopic12Selected] = useState(false);
    const [topic13Selected, setTopic13Selected] = useState(false);
    const [topic14Selected, setTopic14Selected] = useState(false);





    // すべてのトピックを `false` にする関数
    const setAllTopicsFalse = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
    };
    const handleTopic1Click = () => {
        
        setAllTopicsFalse();
        setTopic1Selected(true);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        
        setTryingNo(0);
        initNo.current = 0;
    };
    const handleTopic2Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(true);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 5;
        setTryingNo(5);
    };
    const handleTopic3Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(true);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 10;
        setTryingNo(10);
    };
    const handleTopic4Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(true);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 15;
        setTryingNo(15);
    };
    const handleTopic5Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(true);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 20;
        setTryingNo(20);
    };
    const handleTopic6Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(true);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 25;
        setTryingNo(25);
    };
    const handleTopic7Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(true);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 30;
        setTryingNo(30);
    };
    const handleTopic8Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(true);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 35;
        setTryingNo(35);
    };
    const handleTopic9Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(true);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 40;
        setTryingNo(40);
    };
    const handleTopic10Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(true);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 45;
        setTryingNo(45);
    };
    const handleTopic11Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(true);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 50;
        setTryingNo(50);
    };
    const handleTopic12Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(true);
        setTopic13Selected(false);
        setTopic14Selected(false);
        initNo.current = 55;
        setTryingNo(55);
    };
    const handleTopic13Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(true);
        setTopic14Selected(false);
        initNo.current = 60;
        setTryingNo(60);
    };
    const handleTopic14Click = () => {
        setTopic1Selected(false);
        setTopic2Selected(false);
        setTopic3Selected(false);
        setTopic4Selected(false);
        setTopic5Selected(false);
        setTopic6Selected(false);
        setTopic7Selected(false);
        setTopic8Selected(false);
        setTopic9Selected(false);
        setTopic10Selected(false);
        setTopic11Selected(false);
        setTopic12Selected(false);
        setTopic13Selected(false);
        setTopic14Selected(true);
        initNo.current = 65;
        setTryingNo(65);
    };
    // サイドメニュー関連のステート
    const [listOpen, setListOpen] = useState([false, false, false, false]); // サイドメニューの各カテゴリの開閉状態
    const [tryingCategory, setTryingCategory] = useState<number | null>(null); // 現在試しているカテゴリ
    
    // 解説リストと展開状態の管理
    const [expList, setExpList] = useState<{no: Number, test: TestType, answer: number[], isCorrect: boolean}[]>([]); // 解説リスト
    const [expanded, setExpanded] = useState<boolean[]>([]); // 各解説の展開状態
    
    /**
     * 解説リストを作成する関数
     * @param {string | null} pattern - 表示する解説のフィルタ条件（全て、正解、不正解、未回答）
     */
    const createExpList = (pattern: string | null) => {
        const tmpExpList: {no: Number, test: TestType, answer: number[], isCorrect: boolean}[] = [];
        tests?.forEach((t, idx) => {
            // フィルタ条件に応じて解説リストを作成
            if (
                (pattern === 'all' || pattern === null) // 全て表示
                || (pattern === 'correct' && results[idx]) // 正解のみ表示
                || (pattern === 'incorrect' && !results[idx]) // 不正解のみ表示
                || (pattern === 'unanswered' && userAnswers[idx]?.length === 0) // 未回答のみ表示
            ) {
                tmpExpList.push({no: idx, test: t, answer: userAnswers[idx], isCorrect: results[idx]});
            }
        });
        setExpList(tmpExpList); // 解説リストをステートに設定
        const tmp: boolean[] = Array(tmpExpList?.length).fill(false); // 全て折りたたんだ状態で初期化
        setExpanded(tmp);
    };
    
    // 解説フィルタの切り替え用ステート
    const [alignment, setAlignment] = useState<string | null>('all'); // 現在のフィルタ条件
    
    /**
     * 解説フィルタを切り替える関数
     * @param {React.MouseEvent<HTMLElement>} event - イベントオブジェクト
     * @param {string | null} newAlignment - 新しいフィルタ条件
     */
    const handleAlignment = (
        event: React.MouseEvent<HTMLElement>,
        newAlignment: string | null,
    ) => {
        setAlignment(newAlignment); // フィルタ条件を更新
        createExpList(newAlignment); // フィルタ条件に基づいて解説リストを更新
    };
    
    /**
     * ユーザーの選択肢を設定する関数
     * @param {number} num - 選択した選択肢の番号
     */
    const selectChoice = (num: number) => {
        if (tests[initNo.current].answer?.length === 1) {
            // ラジオボタンの場合（単一選択）
            setSelectedChoice([num]);
        } else {
            // チェックボックスの場合（複数選択）
            if (selectedCoice?.includes(num)) {
                // 選択肢が既に選ばれている場合は削除
                const tmpCoices = selectedCoice?.filter(s => s !== num);
                setSelectedChoice(tmpCoices||[]);
            } else if (selectedCoice?.length < tests[initNo.current]?.answer?.length) {
                // 未選択の場合または最大選択数未満の場合は追加
                setSelectedChoice([...selectedCoice, num]);
            }
        }
    };
    
    /**
     * 次の問題に進む関数
     */
    const goNext = () => {
        const tmpAnswers: number[][] = [];
        for (let i = 0; i < tests?.length; i++) {
            if (i === initNo.current) {
                tmpAnswers.push(selectedCoice); // 現在の回答を保存
            } else {
                tmpAnswers.push(userAnswers[i]); // 他の回答を保持
            }
        }
        setUserAnswers(tmpAnswers); // ユーザー回答を更新
        setTryingNo(initNo.current += 1); // 次の問題番号に進む
    }
    
    /**
     * 前の問題に戻る関数
     */
    /**
     * 前の問題に戻る関数
     * 現在の問題番号を1つ減らす
     */
    const goBack = () => {
        setTryingNo(initNo.current -= 1); // 問題番号をデクリメント
    };
    
    /**
     * 現在の問題番号が変更されたときの処理
     * 現在の問題に対するユーザーの選択をステートにセット
     */
    useEffect(() => {
        setSelectedChoice(userAnswers[initNo.current]||[]); // 現在の問題の回答をセット
    }, [initNo.current]); // `tryingNo`が変更されるたびに実行
    
    /**
     * 問題集またはテストが変更されたときの初期化処理
     * - 初めて問題集を開いた際にユーザーの回答ステートをリセット
     */
    useEffect(() => {
        if (initNo.current === 0) {
            const newUa: number[][] = []; // 初期化用の空の配列
            for (let i = 0; i < tests?.length; i++) {
                newUa.push([]); // 各問題ごとに空の配列を追加
            }
            
            // setUserAnswers(newUa); // 初期化した回答配列をセット
            setStartTime(new Date().getTime());
        }
    }, [questionBook, tests]); // `questionBook`または`tests`が変更されるたびに実行
    
    /**
     * サイドメニューのリストを開く処理
     * - 現在の問題集に基づいてカテゴリの開閉状態を設定
     */
    useEffect(() => {
        if (questionBook) {
            const openCheck: boolean[] = []; // 各カテゴリの開閉状態を記録
            practiceList?.forEach((p, idx) => {
                // 現在の問題集が含まれているカテゴリを確認
                openCheck.push(p?.filter(q => q.practiceId === questionBook).length > 0);
                if (p?.filter(q => q.practiceId === questionBook).length > 0) {
                    setTryingCategory(idx); // 試行中のカテゴリをセット
                }
            });
            setListOpen(openCheck); // カテゴリの開閉状態を更新
        }
    }, [questionBook, practiceList]); // `questionBook`または`practiceList`が変更されるたびに実行
    
    // 回答送信処理
    const updateUserQuestion = async(score: number, answers: (string|null)[]) => {
        // サーバーにスコアや試験時間などの情報を送信
        await axios.post(process.env.REACT_APP_BE_HOST + 'workbooks/updateUserQuestion', {
            "questionBook": questionBook,
            "userId": userId,
            "score": score,
            "time": `${Math.floor(endTime/3600000)}:${Math.floor((endTime%3600000)/60000).toString().padStart(2, '0')}:${Math.floor((endTime%60000)/1000).toString().padStart(2, '0')}`,
            "executedAt": new Date(),
            "questionNum": answers.length,
            "choiceNum1": answers[0] ?? null,
            "choiceNum2": answers[1] ?? null,
            "choiceNum3": answers[2] ?? null,
            "choiceNum4": answers[3] ?? null,
            "choiceNum5": answers[4] ?? null,
            "choiceNum6": answers[5] ?? null,
            "choiceNum7": answers[6] ?? null,
            "choiceNum8": answers[7] ?? null,
            "choiceNum9": answers[8] ?? null,
            "choiceNum10": answers[9] ?? null,
            "choiceNum11": answers[10] ?? null,
            "choiceNum12": answers[11] ?? null,
            "choiceNum13": answers[12] ?? null,
            "choiceNum14": answers[13] ?? null,
            "choiceNum15": answers[14] ?? null,
            "choiceNum16": answers[15] ?? null,
            "choiceNum17": answers[16] ?? null,
            "choiceNum18": answers[17] ?? null,
            "choiceNum19": answers[18] ?? null,
            "choiceNum20": answers[19] ?? null,
            "choiceNum21": answers[20] ?? null,
            "choiceNum22": answers[21] ?? null,
            "choiceNum23": answers[22] ?? null,
            "choiceNum24": answers[23] ?? null,
            "choiceNum25": answers[24] ?? null,
            "choiceNum26": answers[25] ?? null,
            "choiceNum27": answers[26] ?? null,
            "choiceNum28": answers[27] ?? null,
            "choiceNum29": answers[28] ?? null,
            "choiceNum30": answers[29] ?? null,
            "choiceNum31": answers[30] ?? null,
            "choiceNum32": answers[31] ?? null,
            "choiceNum33": answers[32] ?? null,
            "choiceNum34": answers[33] ?? null,
            "choiceNum35": answers[34] ?? null,
            "choiceNum36": answers[35] ?? null,
            "choiceNum37": answers[36] ?? null,
            "choiceNum38": answers[37] ?? null,
            "choiceNum39": answers[38] ?? null,
            "choiceNum40": answers[39] ?? null,
            "choiceNum41": answers[40] ?? null,
            "choiceNum42": answers[41] ?? null,
            "choiceNum43": answers[42] ?? null,
            "choiceNum44": answers[43] ?? null,
            "choiceNum45": answers[44] ?? null,
            "choiceNum46": answers[45] ?? null,
            "choiceNum47": answers[46] ?? null,
            "choiceNum48": answers[47] ?? null,
            "choiceNum49": answers[48] ?? null,
            "choiceNum50": answers[49] ?? null,
            "choiceNum51": answers[50] ?? null,
            "choiceNum52": answers[51] ?? null,
            "choiceNum53": answers[52] ?? null,
            "choiceNum54": answers[53] ?? null,
            "choiceNum55": answers[54] ?? null,
            "choiceNum56": answers[55] ?? null,
            "choiceNum57": answers[56] ?? null,
            "choiceNum58": answers[57] ?? null,
            "choiceNum59": answers[58] ?? null,
            "choiceNum60": answers[59] ?? null,
            "choiceNum61": answers[60] ?? null,
            "choiceNum62": answers[61] ?? null,
            "choiceNum63": answers[62] ?? null,
            "choiceNum64": answers[63] ?? null,
            "choiceNum65": answers[64] ?? null,
            "choiceNum66": answers[65] ?? null,
            "choiceNum67": answers[66] ?? null,
            "choiceNum68": answers[67] ?? null,
            "choiceNum69": answers[68] ?? null,
            "choiceNum70": answers[69] ?? null,
        }, {
            withCredentials: true // クッキー情報を含める
        })
        .then(() => {
            setIsSent(true); // 回答送信済みフラグを設定
            // 一時保存データ削除
            axios.post(process.env.REACT_APP_BE_HOST + 'tmpData/InterruptedDelete', {
                "userId": userData.userId,
            }, {
                withCredentials: true
            }).then((res: any) => {}).catch((e: any) => console.log(e));
        });
    };
    
    // 画面サイズに応じたレイアウト調整
    const breakWidth = 1200; // ブレイクポイントの幅
    const [innerWidth, setInnerWidth] = useState(window.innerWidth); // ウィンドウ幅
    const [isShowSide, setIsShowSide] = useState(false); // サイドメニューの表示状態 
    
    const useWindowSize = (): number[] => {
        const [size, setSize] = useState([0, 0]);
        useLayoutEffect(() => {
            const updateSize = (): void => {
                setSize([window.innerWidth, window.innerHeight]);
            };
    
            window.addEventListener("resize", updateSize);
            updateSize();
    
            return () => window.removeEventListener("resize", updateSize);
        }, []);
        return size;
    };
    const [width, height] = useWindowSize();

    // ウィンドウサイズが変更された場合に状態を更新
    useEffect(() => {
        setInnerWidth(window.innerWidth);
    }, [window.innerWidth, width]);
    
    // ページ離脱時の警告表示
    function beforeUnload(event: BeforeUnloadEvent){
        event.preventDefault();
        event.returnValue = ''; // 警告を表示
    }
    
    function cancelBeforeUnload(event: BeforeUnloadEvent){}
    
    // テストフェーズ時の処理
    useEffect(() => {
        if(phase === 'test') {
            window.onbeforeunload = beforeUnload; // ページ離脱警告を有効化
        } else {
            window.onbeforeunload = cancelBeforeUnload; // ページ離脱警告を無効化
        }
        if (!isSent && phase === 'result') {
            // 採点処理
            initNo.current = 0;
            const resultList: {id: number, isCorrect: boolean}[] = [];
            tests?.forEach((t, index) => {
                let isCorrect = true; // 正解判定
                t.answer?.forEach(a => {
                    if (!userAnswers[index]?.includes(a)) isCorrect = false;
                });
                userAnswers[index]?.forEach(u => {
                    if (!t.answer?.includes(u)) isCorrect = false;
                });
                if(userAnswers[index]?.length === 0) {
                    isCorrect = false; // 未回答は不正解
                }
                resultList.push({id: t.testId, isCorrect});
            });
    
            const tmpResults: boolean[] = [];
            let tmpScore = 0;
            resultList?.forEach(r => {
                tmpResults.push(r.isCorrect);
                if (r.isCorrect) tmpScore++; // スコアを加算
            });

            setResults(tmpResults); // 結果を設定
            setScore(tmpScore); // スコアを設定

            // 保存用回答リスト
            const answerList: TmpAnswerType[] = [];
            tests?.forEach((t, idx) => {
                let answerStr: (string|null) = null;
                if (userAnswers[idx]?.length > 0) {
                    const answerArr: string[] = [];
                    t.choices?.forEach((c, i) => {
                        if (userAnswers[idx].includes(i)) {
                            answerArr.push(String(c.choiceNum));
                        }
                    })
                    answerStr = answerArr.join();
                }
                answerList.push({id: t.testId, answer: answerStr});
            })
            let sortedAnswerList = answerList.sort(function(a, b) {
                return (a.id < b.id) ? -1 : 1;  //testIdの昇順ソート
            });
            const savingAnswerList: (string|null)[] = [];
            sortedAnswerList?.forEach(s => savingAnswerList.push(s.answer));
            if (!isAnswered) {
                updateUserQuestion(tmpScore, savingAnswerList); // 回答結果を送信
            }
        }
    }, [phase, click]);
    
    // 採点結果が更新された場合、問題レビューリストを作成
    useEffect(() => {
        if(results?.length > 0) {
            createExpList('all');
        }
    }, [results]);
    
    // テストの進捗や残り時間を更新するタイマー
    const [progress, setProgress] = useState(0); // テストの進捗率
    const [leftTime, setLeftTime] = useState(""); // 残り時間
    
    useEffect(() => {
        // 時間制限がない問題集の場合、タイマー制御を行わない。
        if (questionBook && (!allowed.includes(questionBook))) {
            const timer = setInterval(() => {
                setProgress((oldProgress) => {
                    const pt = new Date().getTime() - startTime; // 経過時間
                    if (phase === 'test') {
                        setEndTime(pt);
                        if (oldProgress === 100) {
                            alert('時間切れです。テスト終了します。'); // 時間切れ通知
                            setEndTime(testTime);
                            setPhase("result"); // フェーズを結果表示に切り替え
                        }
                    }
                    const left = pt / testTime * 100; // 進捗率を計算
                    const lt = testTime - pt + 1000; // 残り時間を計算
                    setLeftTime(`${Math.floor(lt/3600000)}:${Math.floor((lt%3600000)/60000).toString().padStart(2, '0')}:${Math.floor((lt%60000)/1000).toString().padStart(2, '0')}`);
        
                    return Math.min(left, 100); // 進捗率を制限
                });
            }, 1000);
        
            return () => {
                if(!allowed.includes(questionBook)){
                    clearInterval(timer); // コンポーネントがアンマウントされた際にタイマーを解除
                }
            };
        }
    }, [endTime, phase]);
    
    // Excelファイルのダウンロード処理
    const handlerClickDownloadButton = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
    
        const workbook = new ExcelJS.Workbook(); // Excelワークブックを作成
        workbook.addWorksheet("sheet1");
        const worksheet = workbook.getWorksheet("sheet1");
        if (!worksheet) return;
    
        worksheet.columns = [
            { header: "", key: "c1" },
            { header: "", key: "c2" },
            { header: "", key: "c3" }
        ];
    
        const tmpRows = [
            {c1: practice?.practiceName},
            {c1: "受験者", c2: userData.email, c3: userData.companyName},
            {c1: score / tests?.length < (tryingCategory ? passRatio[tryingCategory] : 0.72) ? '不合格' : '合格', c2: `(${Math.floor(score / tests?.length * 100)}%)`, c3: `${score} / ${tests?.length} 点`},
            {c1: isAnswered ? executedAt.replaceAll('-','/') : (String(new Date().toLocaleDateString())), 
             c3: questionBook > 7 ? ''
               : (endTime > 60 * 60 * 1000 ? 
                `試験時間：${Math.floor(endTime/3600000)}時間${Math.floor((endTime%3600000)/60000)}分`
                : `試験時間：${Math.floor((endTime%3600000)/60000)}分`)
                },
        ];
    
        // 問題ごとの結果を追記
        expList?.forEach((exp, idx) => {
            tmpRows.push({c1: "", c2: "", c3: ""});
            tmpRows.push({c1: "問" + (idx + 1), c2: exp.isCorrect ? "正解" : "不正解", c3: exp.test.question});
            exp.test.choices?.forEach((a, ia) => {
                tmpRows.push({
                    c1: userAnswers[idx]?.includes(ia) ? "選択" : "",
                    c2: tests[idx].answer?.includes(ia) && questionBook<7 ?"★" : "",
                    c3: a.choiceText});
            });
        });
    
        worksheet.addRows(tmpRows); // 行を追加
    
        const uint8Array = await workbook.xlsx.writeBuffer();
        const blob = new Blob([uint8Array], { type: "application/octet-binary" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `【回答】${practice?.practiceName}.xlsx`; // ファイル名を設定
        a.click();
        a.remove();
    };

    // 初回データ取得
    useEffect(() => {
        if (props.tests) {
            const tmpTest: TestType[] = [];
            props.tests?.forEach((t) => {
                tmpTest.push({
                    testId: t.testId,
                    question: t.question,
                    choices: [...t.choices],
                    answer: [...t.answer],
                    explanation: t.explanation,
                    images: t.images ? [...t.images] : [],
                })
            })
            setTests(tmpTest);
            setImages(tmpTest)
        }
    }, [props.tests, props.questionBook]);

    //問題集変更
    const [isGetReady, setIsGetReady] = useState(0);
    const changeProblem = async (id: number, flg: number) => {
    // 問題集を初回に表示時に別問題を選択するとメッセージを表示する。
    if (questionBook !== id) {
        if (initCount.current >= 1) {
            const newPracticeName = practiceList.flat().find(p => p.practiceId === id)?.practiceName ?? 'この問題集';
            const confirmChange = window.confirm(`現在の状態を破棄して『${newPracticeName}』を表示しますか？`);
            if (!confirmChange) return;
        } else {
            initCount.current += 1;
        }
    }
        let isPreview = false; // 実施済みフラグ
        setQuestionBook(id);
        await axios.post(process.env.REACT_APP_BE_HOST + 'workbooks/questions', {
            "userId": userData.userId,
            "questionBook": id,
            "noImage": true,
        }, {
            withCredentials: true
        })
        .then((res: any) => {
            // 過去回答取得
            axios.post(process.env.REACT_APP_BE_HOST + 'tmpData/InterruptedDataPrevious', {
                "userId": userData.userId,
                "questionBook": id,
            }, {
                withCredentials: true
            })
            .then((old: any) => {
                isPreview = !!old.data && questionBook !== id;
                if (questionBook !== id) {
                    // 別問題が選択された場合、再テストボタン押下フラグをfalseに戻す
                    setIsRepTestFlg(false);
                }
                setIsAnswered(isPreview);
                const tmpQuestions: TestType[] =[];
                res.data?.map((q: any, i: number) => {
                    const choices: ChoiceType[] = q.choices;
                    if (!isPreview) {
                        if (allowed.includes(id)) {
                            choices?.sort((a: ChoiceType, b: ChoiceType) => a.choiceNum - b.choiceNum);
                        } else {
                            choices?.sort((a: ChoiceType, b: ChoiceType) => 0.5 - Math.random());
                        }
                    }
                    const answer: number[] = [];
                    choices?.forEach((c: ChoiceType, index) => {
                        if (c.isCorrect) {
                            answer.push(index);
                        }
                    })
                    tmpQuestions.push({
                        testId: q.questionId,
                        question: q.questionText ? String(q.questionText).replaceAll('\\r','\r').replaceAll('\\n','\n') : '',
                        choices: choices,
                        answer: answer,
                        explanation: q.explanation ? String(q.explanation).replaceAll('\\r','\r').replaceAll('\\n','\n') : '',
                        images: [],
                    })
                })
                if(!isPreview) {
                    if (!allowed.includes(id)) {
                        tmpQuestions?.sort((a: TestType, b: TestType) => 0.5 - Math.random());
                    } else {
                        tmpQuestions?.sort(function(a, b) {
                            return (a.testId < b.testId) ? -1 : 1;  //testIdの昇順ソート
                        });
                    }
                } else {
                    tmpQuestions?.sort(function(a, b) {
                        return (a.testId < b.testId) ? -1 : 1;  //testIdの昇順ソート
                    });
                }
                if(isPreview) {
                    const tmpUas: number[][] = [];
                    for(let i = 0; i < tmpQuestions?.length; i++) {
                        let uaStr: string|null = null;
                        switch(i) {
                            case 0: uaStr = old.data?.choiceNum1; break;
                            case 1: uaStr = old.data?.choiceNum2; break;
                            case 2: uaStr = old.data?.choiceNum3; break;
                            case 3: uaStr = old.data?.choiceNum4; break;
                            case 4: uaStr = old.data?.choiceNum5; break;
                            case 5: uaStr = old.data?.choiceNum6; break;
                            case 6: uaStr = old.data?.choiceNum7; break;
                            case 7: uaStr = old.data?.choiceNum8; break;
                            case 8: uaStr = old.data?.choiceNum9; break;
                            case 9: uaStr = old.data?.choiceNum10; break;
                            case 10: uaStr = old.data?.choiceNum11; break;
                            case 11: uaStr = old.data?.choiceNum12; break;
                            case 12: uaStr = old.data?.choiceNum13; break;
                            case 13: uaStr = old.data?.choiceNum14; break;
                            case 14: uaStr = old.data?.choiceNum15; break;
                            case 15: uaStr = old.data?.choiceNum16; break;
                            case 16: uaStr = old.data?.choiceNum17; break;
                            case 17: uaStr = old.data?.choiceNum18; break;
                            case 18: uaStr = old.data?.choiceNum19; break;
                            case 19: uaStr = old.data?.choiceNum20; break;
                            case 20: uaStr = old.data?.choiceNum21; break;
                            case 21: uaStr = old.data?.choiceNum22; break;
                            case 22: uaStr = old.data?.choiceNum23; break;
                            case 23: uaStr = old.data?.choiceNum24; break;
                            case 24: uaStr = old.data?.choiceNum25; break;
                            case 25: uaStr = old.data?.choiceNum26; break;
                            case 26: uaStr = old.data?.choiceNum27; break;
                            case 27: uaStr = old.data?.choiceNum28; break;
                            case 28: uaStr = old.data?.choiceNum29; break;
                            case 29: uaStr = old.data?.choiceNum30; break;
                            case 30: uaStr = old.data?.choiceNum31; break;
                            case 31: uaStr = old.data?.choiceNum32; break;
                            case 32: uaStr = old.data?.choiceNum33; break;
                            case 33: uaStr = old.data?.choiceNum34; break;
                            case 34: uaStr = old.data?.choiceNum35; break;
                            case 35: uaStr = old.data?.choiceNum36; break;
                            case 36: uaStr = old.data?.choiceNum37; break;
                            case 37: uaStr = old.data?.choiceNum38; break;
                            case 38: uaStr = old.data?.choiceNum39; break;
                            case 39: uaStr = old.data?.choiceNum40; break;
                            case 40: uaStr = old.data?.choiceNum41; break;
                            case 41: uaStr = old.data?.choiceNum42; break;
                            case 42: uaStr = old.data?.choiceNum43; break;
                            case 43: uaStr = old.data?.choiceNum44; break;
                            case 44: uaStr = old.data?.choiceNum45; break;
                            case 45: uaStr = old.data?.choiceNum46; break;
                            case 46: uaStr = old.data?.choiceNum47; break;
                            case 47: uaStr = old.data?.choiceNum48; break;
                            case 48: uaStr = old.data?.choiceNum49; break;
                            case 49: uaStr = old.data?.choiceNum50; break;
                            case 50: uaStr = old.data?.choiceNum51; break;
                            case 51: uaStr = old.data?.choiceNum52; break;
                            case 52: uaStr = old.data?.choiceNum53; break;
                            case 53: uaStr = old.data?.choiceNum54; break;
                            case 54: uaStr = old.data?.choiceNum55; break;
                            case 55: uaStr = old.data?.choiceNum56; break;
                            case 56: uaStr = old.data?.choiceNum57; break;
                            case 57: uaStr = old.data?.choiceNum58; break;
                            case 58: uaStr = old.data?.choiceNum59; break;
                            case 59: uaStr = old.data?.choiceNum60; break;
                            case 60: uaStr = old.data?.choiceNum61; break;
                            case 61: uaStr = old.data?.choiceNum62; break;
                            case 62: uaStr = old.data?.choiceNum63; break;
                            case 63: uaStr = old.data?.choiceNum64; break;
                            case 64: uaStr = old.data?.choiceNum65; break;
                            case 65: uaStr = old.data?.choiceNum66; break;
                            case 66: uaStr = old.data?.choiceNum67; break;
                            case 67: uaStr = old.data?.choiceNum68; break;
                            case 68: uaStr = old.data?.choiceNum69; break;
                            case 69: uaStr = old.data?.choiceNum70; break;
                            default: break;
                        }
                        if(uaStr) {
                            const ua: number[] = [];
                            uaStr.split(',').forEach(a => {
                                ua.push(
                                    tmpQuestions[i]?.choices?.findIndex(c => c.choiceNum ===Number(a))
                                );
                            })
                            tmpUas.push(ua);
                        } else {
                            tmpUas.push([]);
                        }
                    }
                    if(flg === 0){
                        setUserAnswers(tmpUas);
                    }
                }
                setTests(tmpQuestions);
                setIsGetReady(id);
             
                let practicesN:PracticeType[] = [];
                practiceList?.forEach(list => {
                    const pr = list.find(p => p.practiceId === id);
                    if (pr) {
                        practices = [...list]
                    }
                })
                const practiceN = practices.find(p => p.practiceId === id)
                let nextIdN: null | number = null;
                let curFlgN = false;
                practicesN?.forEach((p, idx) => {
                    if (idx + 1 < practicesN?.length && curFlgN) {
                        nextIdN = p.practiceId;
                        curFlgN = false;
                    } else if (p.practiceId === practiceN?.practiceId) {
                        curFlgN = true;
                    }
                })
                setNextId(nextIdN);
                setStartTime(now.getTime());
                const etArr = isPreview ? old.data.time?.split(':') : [];
                setEndTime(isPreview ? etArr[0] * 3600000 + etArr[1] * 60000  + etArr[2] * 1000 : 0);
                setPhase(isPreview ? 'result' : 'test');
                setTryingNo(isPreview ? tmpQuestions?.length : 0);
                setSelectedChoice([]);
                if (!isPreview) {
                    const uaN: number[][] = [];
                    for (let i = 0; i < tests?.length; i++) {
                        uaN.push([]);
                    }
                    if(flg === 0){
                        setUserAnswers(ua);
                    }
                } else {
                    setClick(click + 1);
                    setExecutedAt(old.data?.executedAt);
                }
                setScore(0);
                setResults([]);
                setIsSent(false);
            
                setOpen(false);

                setExpList([]);
                setExpanded([]);
            
                setAlignment('all');
            }).catch((e: any)=> console.log(e));
        })
        .catch((e: any)=> console.log(e));
    }

    //問題集変更時の解説画像取得
    const getImages = async() => {
        await axios.post(process.env.REACT_APP_BE_HOST + 'workbooks/questions', {
            "userId": userData.userId,
            "questionBook": questionBook,
            "noImage": false,
        }, {
            withCredentials: true
        })
        .then((res: any) => {
            if(isGetReady === questionBook) {
                const tmpQuestions: TestType[] =[];
                tests?.forEach((q => {
                    const question = res.data?.find((d: any) => d.questionId === q.testId);
                    tmpQuestions.push({
                        ...q,
                        images: question.images,
                    })
                }));
                
                setImages(tmpQuestions);
            }
        })
        .catch((e: any)=> console.log(e));
    }
    useEffect(() => {
        if(isGetReady > 0 && ((phase === 'test' && initNo.current > 0) || phase === 'exp')) {
            setIsGetReady(0)
            getImages();
        }
    }, [isGetReady, phase, initNo.current])

    /**
     * 中断データ保存
     */
    const saveAnswer = () => {
        if (allowed.includes(questionBook)) {
            if (window.confirm("回答を一時保存しますか？")) {
                const tmpOrder: number[] = [];
                const tmpNums: (string | null)[] = [];
                const tmpQuestionNums: number[] = [];
                
                tests?.forEach((t, idx) => {
                    tmpOrder.push(t.testId);
                    const tmpNum: (string | null)[] = [];

                    if (idx === initNo.current) {
                        selectedCoice?.forEach(c => {
                            tmpNum.push(String(t.choices[c]?.choiceNum));
                        });
                    } else {
                        userAnswers[idx]?.forEach(a => {
                            tmpNum.push(String(t.choices[a].choiceNum));
                        });
                    }

                    tmpNums.push(tmpNum?.length > 0 ? tmpNum?.join() : null);
                    tmpQuestionNums.push(initNo.current);
                })
                axios.post(process.env.REACT_APP_BE_HOST + 'tmpData/InterruptedSave', {
                    "userId": userData.userId,
                    "questionBook": questionBook,
                    "questionNums": [initNo.current],
                    "choiceNums": [tmpNums],
                    "questionOrder": tmpOrder,
                    "elapsed": `00:00:00`
                }, {
                    withCredentials: true
                })
                .then(() => window.alert('回答データを保存しました。'))
                .catch(() => window.alert('回答データの保存に失敗しました'));
            }
        } else {
            if (window.confirm("回答を一時保存しますか？\n\n※保存された時点の残り時間から、テストが再開されます。")) {
                const tmpOrder: number[] = [];
                const tmpNums: (string|null)[] = [];
                const tmpQuestionNums: number[] = [];
                tests?.forEach((t, idx) => {
                    tmpOrder.push(t.testId);
                    const tmpNum: (string|null)[] = [];
                    if (idx === initNo.current) {
                        selectedCoice?.forEach(c => {
                            tmpNum.push(String(t.choices[c]?.choiceNum));
                        })
                    } else {
                        userAnswers[idx]?.forEach(a => {
                            tmpNum.push(String(t.choices[a].choiceNum));
                        })
                    }
                    tmpNums.push(tmpNum?.length > 0 ? tmpNum?.join(): null);
                    tmpQuestionNums.push(initNo.current);
                })
                axios.post(process.env.REACT_APP_BE_HOST + 'tmpData/InterruptedSave', {
                    "userId": userData.userId,
                    "questionBook": questionBook,
                    "questionNums": [initNo.current],
                    "choiceNums": [tmpNums],
                    "questionOrder": tmpOrder,
                    "elapsed": `${('00' + Math.floor(endTime/3600000)).slice(-2)}:${('00' + Math.floor((endTime%3600000)/60000)).slice(-2)}:${('00' + Math.floor((endTime%3600000)/1000)).slice(-2)}`
                }, {
                    withCredentials: true
                })
                .then((res: any) => window.alert('回答データを保存しました。'))
                .catch((e) => window.alert('回答データの保存に失敗しました'))
            }
        }
    }

    // PDF出力
    const { handlePrintPDF } = usePdfExport(<Certificate1PDF title={practice?.practiceName || ''} category={tryingCategory}/>)

    // フルスクリーン
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isFullScreenChanged, setIsFullScreenChanged] = useState(false);
    const handleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
        setIsFullScreenChanged(true);
    }
    const fullScreenChanged = async() => {
        const _sleep = (ms:number) => new Promise((resolve) => setTimeout(resolve, ms));
        await _sleep(200);
        setIsFullScreenChanged(false);
    }
    useEffect(() => {
        if (isFullScreenChanged) {
            fullScreenChanged();
        }
    }
    , [isFullScreenChanged]);


    return (
        <UserFrame
        title={
            !!practice?.practiceName ? 
                innerWidth > 900 ? practice?.practiceName : practice?.practiceName
            : '問題集選択'}
        isMargin
        >
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Typography id="modal-modal-title" variant="h6" component="h2">
                        終了しますか？
                    </Typography>
                    <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                        {userAnswers?.filter(a => a?.length === 0)?.length > 0 ? 
                             `${userAnswers?.filter(a => a?.length === 0)?.length}問スキップしました。`
                            :
                            "まだ入力した問題を見直すことができます。"
                        }
                    </Typography>
                    <Box mt={2}>
                        <div style={{display: 'inline-block', float: 'right'}}>
                            <Button sx={{mr:2}} onClick={()=>setOpen(false)}>問題を見直す</Button>
                            <Button
                                onClick={() => {
                                    setPhase('result');
                                    setOpen(false);
                                    setIsCompleted(true);
                                    isCompletedRef.current = true;
                                    props.onTestComplete?.();
                                    setIsTopicOpen(false);
                                }}
                                variant='contained'
                            >
                                テストを完了する
                            </Button>
                        </div>
                    </Box>
                </Box>
            </Modal>

            <Grid container maxWidth={isFullScreen ? undefined : 1520} marginRight={-260}>
                <Grid item xs={isFullScreen ? 12 :(innerWidth <= breakWidth && !isShowSide ? 11.7 : 8)} lg={isFullScreen ? 12 :8}>
                <Container maxWidth={isFullScreen ? false : 'lg'}>
                    <Box sx={{padding: 1}}>
                        <Box overflow='scroll' sx={{height: isFullScreen ? '72vh' : '480px'}} px={2}>
                            {!questionBook && <Box sx={{textAlign: 'center', marginTop: '200px', fontSize: 26}}>
                                サイドメニューから問題集を選択してください。
                            </Box>
                            }
                    {allowed.includes(questionBook) && tests?.length > 0 ? (
                        <>
                            <Box>
                                {phase === 'test' && tests?.length > 0 && (
                                    <>
                                        <Stack direction={'row'}>
                                            <Box p={2}>{initNo.current + 1}/{tests?.length}</Box>
                                            <Box mt={3.4} width={"50%"}>
                                                <LinearProgress variant="determinate" value={progress} />
                                            </Box>
                                            
                                            <Box m={1}>
                                                <Button
                                                    sx={{ marginLeft: 1 }}
                                                    variant='outlined'
                                                    onClick={saveAnswer}
                                                >
                                                    一時保存
                                                </Button>
                                            </Box>
                                        </Stack>
                                        {topic1Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current]?.choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={!!selectedCoice ? selectedCoice.includes(idx): false}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic2Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic3Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic4Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic5Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic6Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic7Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic8Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic9Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic10Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic11Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic12Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic13Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                        {topic14Selected === true && phase === 'test' && tests.length > 0 && (
                                            <>
                                                <Box>☆問題{initNo.current + 1}:</Box>
                                                <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                                    {tests[initNo.current]?.question}
                                                </Box>
                                                {tests[initNo.current].choices.map((c, idx) => (
                                                    <Box
                                                        key={`choice_${idx}`}
                                                        border={2}
                                                        borderColor={'lightGrey'}
                                                        p={1}
                                                        my={1}
                                                        onClick={() => selectChoice(idx)}
                                                        sx={{ ":hover": { background: 'lightGrey' } }}
                                                    >
                                                        <Stack direction={'row'}>
                                                            {tests[initNo.current].answer?.length > 1 ? (
                                                                <Checkbox
                                                                    disabled
                                                                    checked={selectedCoice?.includes(idx)}
                                                                />
                                                            ) : (
                                                                <Radio
                                                                    disabled
                                                                    checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                                />
                                                            )}
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <div>{c.choiceText}</div>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                    </>
                                )}
                            </Box>
                        </>
                    ) : (
                        <>
                            {phase === 'test' && tests?.length > 0 && (
                                <>
                                    <Stack direction={'row'}>
                                        <Box p={2}>{initNo.current + 1}/{tests?.length}</Box>
                                        <Box mt={3.4} width={"50%"}>
                                            <LinearProgress variant="determinate" value={progress} />
                                        </Box>
                                        <Box pt={2} pl={2}><AccessTimeIcon /></Box>
                                        <Box pt={1.9} pl={1}>{leftTime}</Box>
                                        <Box m={1}>
                                            <Button
                                                onClick={() => {
                                                    const tmpAnswers = tests.map((_, i) =>
                                                        i === initNo.current ? selectedCoice : userAnswers[i]
                                                    );
                                                    setUserAnswers(tmpAnswers);
                                                    setOpen(true);
                                                }}
                                            >
                                                テストを完了する
                                            </Button>
                                            <Button
                                                sx={{ marginLeft: 1 }}
                                                variant='outlined'
                                                onClick={saveAnswer}
                                            >
                                                一時保存
                                            </Button>
                                        </Box>
                                    </Stack>
                                    <Box>☆問題{tryingNo + 1}:</Box>
                                    <Box py={1} sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                                        {tests[tryingNo]?.question}
                                    </Box>
                                    {tests[tryingNo].choices.map((c, idx) => (
                                        <Box
                                            key={`choice_${idx}`}
                                            border={2}
                                            borderColor={'lightGrey'}
                                            p={1}
                                            my={1}
                                            onClick={() => selectChoice(idx)}
                                            sx={{ ":hover": { background: 'lightGrey' } }}
                                        >
                                            <Stack direction={'row'}>
                                                {tests[tryingNo].answer?.length > 1 ? (
                                                    <Checkbox
                                                        disabled
                                                        checked={selectedCoice?.includes(idx)}
                                                        key={`c${idx}_${String(selectedCoice)}`}
                                                    />
                                                ) : (
                                                    <Radio
                                                        disabled
                                                        checked={selectedCoice ? idx === selectedCoice[0] : false}
                                                        key={`r${idx}_${String(selectedCoice)}`}
                                                    />
                                                )}
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <div>{c.choiceText}</div>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </>
                            )}
                            {phase === 'result' && <>
                                <Box mb={1}>
                                    <Typography id="modal-modal-title" variant="h6" component="h2">
                                        {practice?.practiceName +` - 結果`}
                                    </Typography>
                                </Box>
                                <Divider/>
                                {allowed.includes(questionBook) ? (
                                    <Box my={2}>
                                        &nbsp;
                                    </Box>
                                ) : (
                                    <Box my={2}>
                                    {tests?.length}問｜2時間10分｜正解率72%以上で合格
                                    </Box>
                                )}
                                <Stack direction={'row'} border={1} borderColor={'#ccc'}>
                                    <Box width={'400px'} p={2}>
                                        <DoughnutChart data={[score, tests?.length - score]}/>
                                    </Box>
                                    <Box p={2} mt={5}>
                                        <Stack direction={'row'}>
                                            <Typography mr={1}>{`${practice?.triedCount ? practice?.triedCount + 1 : 1}回目:`}</Typography>
                                            <Typography color={score / tests?.length < (tryingCategory ? passRatio[tryingCategory] : 0.72) ? '#f59787' : '#4dd385'}>
                                                {score / tests?.length < (tryingCategory ? passRatio[tryingCategory] : 0.72) ? '不合格' : '合格'}{`(${(tryingCategory ? passRatio[tryingCategory] : 0.72)*100}%以上で合格)`}
                                            </Typography>
                                        </Stack>
                                        <Stack direction={'row'} my={2}>
                                            <Typography mr={1} variant="h4">{`${Math.floor(score / tests?.length * 100)}%`}</Typography>
                                            <Typography mt={2}>
                                                {`正解 (${score}/${tests?.length})`}
                                            </Typography>
                                        </Stack>
                                        {!allowed.includes(questionBook) && <Typography>
                                            {endTime > 60 * 60 * 1000 ? 
                                            `${Math.floor(endTime/3600000)}時間${Math.floor((endTime%3600000)/60000)}分`
                                            : `${Math.floor((endTime%3600000)/60000)}分`
                                            }
                                        </Typography>}
                                        <Typography >
                                            {isAnswered ? 
                                                `${executedAt.substring(0,4)}年${executedAt.substring(5,7)}月${executedAt.substring(8,10)}日`
                                                :
                                                `${now.getFullYear()}年${now.getMonth()+1}月${now.getDate()}日`
                                            }
                                        </Typography>
                                        <Button sx={{marginTop: '2rem'}} variant='outlined' onClick={()=> {setPhase('exp'); }}>
                                            問題を見直す
                                        </Button>
                                        <Button sx={{marginTop: '2rem', marginLeft: '2rem'}} variant='outlined' onClick={(e)=>handlerClickDownloadButton(e)}>
                                            結果出力
                                        </Button>
                                        {score / tests?.length >= (tryingCategory ? passRatio[tryingCategory] : 0.72) && tryingCategory !== 3 && <Button sx={{marginTop: '2rem', marginLeft: '2rem'}} variant='outlined' onClick={handlePrintPDF}>
                                            修了書発行
                                        </Button>}
                                    </Box>
                                </Stack>
                            </>}
                            {phase === 'exp' && <>
                            <Box mb={14}>
                                <Box mb={1}>
                                    <Typography id="modal-modal-title" variant="h6" component="h2">
                                        {practice?.practiceName +` - 結果`}
                                    </Typography>
                                </Box>
                                <Divider/>
                                <Typography mt={1} variant="h6">{`試行${practice?.triedCount ? practice?.triedCount + 1 : 1}`}</Typography>
                                <div style={{display: 'inline-block', float: 'right'}}>
                                    <Box mb={2}>
                                        <ToggleButtonGroup
                                            value={alignment}
                                            exclusive
                                            onChange={handleAlignment}
                                        >
                                            <ToggleButton value="all">
                                                {`${tests?.length} すべて`}
                                            </ToggleButton>
                                            <ToggleButton value="correct">
                                                {`${score} 正解`}
                                            </ToggleButton>
                                            <ToggleButton value="incorrect">
                                                {`${tests?.length - score} 不正解`}
                                            </ToggleButton>
                                            {/*<ToggleButton value="unanswered">
                                                {`${userAnswers?.filter(a => a?.length === 0)?.length ?? 0} 未回答`}
                                            </ToggleButton>*/}
                                        </ToggleButtonGroup>
                                    </Box>
                                    <div style={{display: 'inline-block', float: 'right'}}>
                                        <Button
                                            onClick={() => {
                                                const tmp: boolean[] = Array(expList?.length);
                                                tmp.fill(false);
                                                setExpanded(tmp);
                                            }}
                                        >
                                            全ての問題を折りたたむ
                                        </Button>
                                    </div>
                                </div>
                            </Box>
                            <Box>
                                {expList && expList?.map((l, idx) => <Accordion
                                    key={`answer_${idx}`}
                                    expanded={expanded[idx]}
                                    onChange={() => {
                                        const tmp: boolean[] = [];
                                        expanded?.forEach((e, i) => {
                                            if (i === idx) {
                                                tmp.push(!expanded[i]);
                                            } else {
                                                tmp.push(expanded[i]);
                                            }
                                        })
                                        setExpanded(tmp);
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        id={`acc_${idx}`}
                                        sx={{
                                            '&:hover': {
                                                bgcolor: '#eeeeee',
                                            },
                                        }}
                                    >
                                        {l.isCorrect ? <span style={{width: '24px'}}/> : <ErrorIcon color='error'/>}
                                        <b>{`問題${Number(l.no) + 1}　`}</b>
                                        {l.isCorrect && userAnswers[idx] ? 
                                            <Typography color={'#4dd385'}>正解</Typography>
                                            : <Typography color={'#f59787'}>不正解<span style={{color: 'red'}}>{(userAnswers[idx]?.length ?? 0) < (l.test.answer?.length ?? 1) ? " -未選択-" : ""}</span></Typography>
                                        }
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box ml={'24px'} sx={{whiteSpace: 'pre-wrap'}}>{l.test.question}</Box>
                                        {l.test.choices && l.test.choices?.map((oc, oic) => {
                                            const c = l.test.choices.filter(item => item.choiceNum === oic + 1)[0];
                                            const ic = l.test.choices.findIndex(item => item.choiceNum === c?.choiceNum);

                                                                                                        const isCorrect = l.test.answer?.includes(ic);
                                            const selectedIds = userAnswers[idx] ?? []; // 選択された選択肢のID配列
                                            const correctIds = l.test.answer ?? [];     // 正解の選択肢ID配列

                                            const isAnswered = selectedIds?.length > 0;
                                            const isCorrectAnswer =
                                            selectedIds?.length > 0 &&
                                            correctIds?.length > 0 &&
                                            correctIds.every(id => selectedIds.includes(id)) &&
                                            selectedIds.every(id => correctIds.includes(id));

                                            const isSingleChoice = correctIds?.length === 1;
                                            return (
                                                <React.Fragment key={`choice_${oic}`}>
                                                    {(questionBook < 5) && (
                                                        <Box p={2} border={3} my={1}
                                                            borderColor={l.test.answer?.includes(ic) ? "#8a9" : 
                                                            userAnswers[idx]?.includes(ic) ? "#c96" : "#ccc"
                                                            }
                                                            bgcolor={l.test.answer?.includes(ic) ? "#efe" :
                                                            userAnswers[idx]?.includes(ic) ? "#fee" : "#fff"
                                                        }>
                                                        {(l.test.answer?.includes(ic) || userAnswers[idx]?.includes(ic)) &&
                                                            (<Box
                                                                px={1} py={0.3} mb={1} ml={2} mt={-1} color={'#eee'}
                                                                bgcolor={l.test.answer?.includes(ic) ? "#acb" : "#e96"}
                                                                width={l.test.answer?.includes(ic) ? "3.1rem": "9.1rem"}
                                                            >
                                                                {l.test.answer?.includes(ic) ? "正解": "回答は不正解です"}
                                                            </Box>)
                                                        }
                                                        <Stack direction={'row'} ml={'6px'}>
                                                            {!userAnswers[idx]?.includes(ic) ?
                                                                (<RadioButtonUncheckedIcon />) :
                                                                    l.test.answer?.includes(ic) ?
                                                                    (<RadioButtonCheckedIcon />) :
                                                                    (<CancelIcon />)
                                                            }
                                                            <Box ml={2}>{c?.choiceNum}. {c?.choiceText}</Box>
                                                        </Stack>
                                                        </Box>
                                                    )}
                                                    {questionBook > 7 && (
                                                        <Box
                                                            p={2} border={3} my={1} 
                                                            borderColor={isAnswered? isCorrectAnswer ? correctIds.includes(ic) ? "#8a9" : "#ccc" : selectedIds.includes(ic) && !correctIds.includes(ic) ? "#c96" : "#ccc" : "#ccc" }
                                                            bgcolor={isAnswered ? isCorrectAnswer ? correctIds.includes(ic) ? "#efe" : "#fff" : selectedIds.includes(ic) && !correctIds.includes(ic) ? "#fee" : "#fff" : "#fff"}
                                                        >
                                                            {/* 正解バッジ */}
                                                            {isAnswered && isCorrectAnswer && correctIds.includes(ic) && (
                                                            <Box
                                                                px={1} py={0.3} mb={1} ml={2} mt={-1} color={"#eee"} bgcolor={"#acb"} width={"3.1rem"}
                                                                >
                                                                正解
                                                            </Box>
                                                            )}

                                                            {/* 不正解バッジ */}
                                                            {isAnswered && !isCorrectAnswer && selectedIds.includes(ic) && !correctIds.includes(ic) && (
                                                            <Box
                                                                px={1} py={0.3} mb={1} ml={2} mt={-1} color={"#eee"} bgcolor={"#e96"} width={"9.1rem"}
                                                                >
                                                                回答は不正解です
                                                            </Box>
                                                            )}

                                                            <Stack direction={"row"} ml={"6px"}>
                                                                {/* ラジオアイコン制御 */}
                                                                {selectedIds.includes(ic) ? (
                                                                    correctIds.includes(ic) ? (
                                                                    isCorrectAnswer ? (
                                                                        <RadioButtonCheckedIcon />
                                                                    ) : (
                                                                        <RadioButtonUncheckedIcon />
                                                                    )
                                                                    ) : (
                                                                        <CancelIcon />
                                                                    )
                                                                ) : (
                                                                    correctIds.includes(ic) && isCorrectAnswer ? (
                                                                        <RadioButtonCheckedIcon />
                                                                    ) : (
                                                                        <RadioButtonUncheckedIcon />
                                                                    )
                                                                )}
                                                                <Box ml={2}>{c?.choiceNum}. {c?.choiceText}</Box>
                                                            </Stack>
                                                        </Box>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}

                                        {questionBook !== 8 && (
                                            <>
                                            <Box ml={'24px'}><b>全体的な説明</b></Box>
                                            <Box ml={'24px'}>
                                                <TextAndImg text={l.test.explanation} images={images[idx]?.images}/>
                                            </Box>
                                            </>
                                        )}
                                    </AccordionDetails>
                                </Accordion>)}
                            </Box>
                            </>}
                        </Box>
                        {!!questionBook ? <Box sx={{ width: '100%', minHeight:'6rem' }}>
                            {phase === 'test' ? <Box p={1}>
                                <div style={{display: 'inline-block'}}>
                                    <Button
                                        onClick={() => {
                                            if (side === 'practices') {
                                                setSide('tests');
                                            } else {
                                                setSide('practices')
                                            }
                                        }}
                                    >
                                        {side === 'practices' ? '全ての問題を見る' : '問題を隠す'}
                                    </Button>
                                </div>
                                <div style={{display: 'inline-block', float: 'right'}}>
                                    <Button sx={{mr: 2}}
                                        onClick={()=>goBack()}
                                        disabled={initNo.current === 0}
                                    >
                                        戻る
                                    </Button>
                                    {initNo.current + 1 < tests?.length ? <Button
                                        onClick={()=> {
                                            goNext();
                                            //window.scrollTo(0,0);
                                        }}
                                        variant={selectedCoice?.length > 0 ? 'contained':'outlined'}
                                    >
                                        {selectedCoice?.length > 0 ? '次の問題 >':'問題を飛ばす >'}
                                    </Button>
                                    :
                                    <Button
                                        onClick={()=> {
                                            const tmpAnswers: number[][] = [];
                                            for (let i = 0; i < tests?.length; i++) {
                                                if (i === tryingNo) {
                                                    tmpAnswers.push(selectedCoice);
                                                } else {
                                                    tmpAnswers.push(userAnswers[i])
                                                }
                                            } 
                                            setUserAnswers(tmpAnswers);
                                            setOpen(true);
                                        }}
                                        variant={'contained'}
                                    >
                                        {`テストを完了する >`}
                                    </Button>}
                                    {!isFullScreenChanged ? 
                                    <Tooltip title={isFullScreen ? '拡大表示を終了' : '拡大表示'} placement='top'>
                                        <IconButton
                                            style={{marginLeft: '0.5rem', marginRight: '-1rem'}}
                                            onClick={handleFullScreen}
                                            
                                        >
                                            {isFullScreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                                        </IconButton>
                                    </Tooltip>
                                    :
                                    <IconButton
                                            style={{marginLeft: '0.5rem', marginRight: '-1rem'}}
                                            onClick={handleFullScreen}
                                        >
                                        {isFullScreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                                    </IconButton>
                                    }
                                </div>
                            </Box>
                            :
                            <Box p={1}>
                                <div style={{display: 'inline-block', float: 'right'}}>
                                    {nextId ? <Button sx={{mr: 2}}
                                        //href={`/problems?no=${nextId}`}
                                        onClick={()=>{ 
                                            changeProblem(nextId,0);
                                        }}
                                    >
                                        続ける
                                    </Button>
                                    :
                                    <Button sx={{mr: 2}}
                                        href={`/home`}
                                        onClick={()=>{ 
                                            window.onbeforeunload = cancelBeforeUnload;
                                        }}
                                    >
                                        Homeへ戻る
                                    </Button>
                                    }
                                    {phase === 'exp' && (
                                        <Button sx={{mr: 2}}
                                            onClick={()=> {
                                                createExpList('all');
                                                setPhase('result');
                                            }}
                                        >
                                            結果の概要に戻る
                                        </Button>
                                    )}
                                    <Button
                                        //href={`/problems?no=${practice?.practiceId}`}
                                        onClick={()=>{ 
                                            changeProblem(questionBook,0);
                                            setIsTopicOpen(true)
                                            setIsRepTestFlg(true);
                                            setIsCompleted(false);
                                            isCompletedRef.current = true;
                                            setIsParentClicked(true);
                                        }}
                                        variant={'contained'}
                                    >
                                        再テスト
                                    </Button>
                                    {!isFullScreenChanged ? 
                                    <Tooltip title={isFullScreen ? '拡大表示を終了' : '拡大表示'} placement='top'>
                                        <IconButton
                                            style={{marginLeft: '0.5rem', marginRight: '-1rem'}}
                                            onClick={handleFullScreen}
                                            
                                        >
                                            {isFullScreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                                        </IconButton>
                                    </Tooltip>
                                    :
                                    <IconButton
                                            style={{marginLeft: '0.5rem', marginRight: '-1rem'}}
                                            onClick={handleFullScreen}
                                        >
                                        {isFullScreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                                    </IconButton>
                                    }
                                </div>
                            </Box>}
                        </Box>
                        : 
                        <Box sx={{ width: '100%', minHeight:'6rem' }}>
                            <div style={{display: 'inline-block', float: 'right'}}>
                            {!isFullScreenChanged ? 
                                <Tooltip title={isFullScreen ? '拡大表示を終了' : '拡大表示'} placement='top'>
                                    <IconButton
                                        style={{marginLeft: '0.5rem', marginRight: '-1rem'}}
                                        onClick={handleFullScreen}
                                        
                                    >
                                        {isFullScreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                                    </IconButton>
                                </Tooltip>
                                :
                                <IconButton
                                        style={{marginLeft: '0.5rem', marginRight: '-1rem'}}
                                        onClick={handleFullScreen}
                                    >
                                    {isFullScreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                                </IconButton>
                            }
                            </div>
                        </Box>
                        }
                    </Box>
                </Container>
                </Grid>

                {innerWidth <= breakWidth && !isShowSide &&
                    <Grid item xs={0.3} paddingTop={30} marginLeft={-2}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="end"
                            onClick={() => setIsShowSide(true)}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                    </Grid>
                }

                {!isFullScreen && (side === 'practices' || phase !== 'test') && (innerWidth > breakWidth || isShowSide) &&
                <Grid item xs={3.7} lg={4}>
                    <Container maxWidth='md'>
                        <Box sx={{overflowY: 'scroll', height: '525px'}}>
                            <List sx={{ width: '100%', maxWidth: 380, bgcolor: 'background.paper' }}>
                                {problemCategory?.map((pc, idx) => {
                                    const labelId = `checkbox-list-label1-${pc}`;
                                    let totalCheck = practiceList[idx]?.length > 0;
                                    practiceList[idx]?.forEach((plt) => {
                                        if (plt.triedCount === 0) totalCheck = false
                                    })

                                    return (
                                        <>
                                            <ListItem
                                                key={`p1_${pc}`}
                                                disablePadding
                                                sx={{backgroundColor: tryingCategory === idx ? '#ddeeff' : '#f8f8f8'}}
                                            >
                                                <ListItemButton
                                                    role={undefined}
                                                    onClick={()=>{
                                                        setListOpen([
                                                            idx === 0 ? !listOpen[0] : listOpen[0],
                                                            idx === 1 ? !listOpen[1] : listOpen[1],
                                                            idx === 2 ? !listOpen[2] : listOpen[2],
                                                            idx === 3 ? !listOpen[3] : listOpen[3], 
                                                        ])
                                                    }}
                                                    dense
                                                >
                                                    <ListItemIcon>
                                                        <Checkbox
                                                            edge="start"
                                                            disabled
                                                            checked={totalCheck} 
                                                            tabIndex={-1}
                                                            disableRipple
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText id={labelId} primary={pc} />
                                                </ListItemButton>
                                            </ListItem>
                                            <Collapse in={listOpen[idx]} timeout="auto" unmountOnExit>
                                                <List component="div" disablePadding>
                                                    {practiceList[idx]?.map((value) => {
                                                        return (
                                                            <ListItem
                                                                key={`p_${value.practiceId}`}
                                                                sx={{
                                                                    backgroundColor: value?.practiceId === questionBook ? '#ddeeff' : '#f8f8f8',
                                                                    display: "block", // これでネスト表示を強制
                                                                }}
                                                            >
                                                                {/* 親の問題リスト */}
                                                                <ListItemButton
                                                                    onClick={() => {
                                                                        const isFirstTry = value.triedCount === 0;
                                                                        const isSamePractice = practice && practice.practiceId === value.practiceId;
                                                                        let isAnswered = false;
                                                                        userAnswers?.forEach((ua, i) => {
                                                                            setIsCompleted(false);
                                                                            isCompletedRef.current = true;
                                                                            if(ua?.length !== 0) isAnswered = true;
                                                                        });
                                                                        if(phase === 'test') {
                                                                            isCompletedRef.current = false;   
                                                                        }
                                                                        if(phase === 'test' && isAnswered) {
                                                                            if(window.confirm('別の問題集を選択しています。この問題集に遷移しますか?\n※回答内容は保存されません')===false) return
                                                                        }
                                                                        if (!isSamePractice) {
                                                                            changeProblem(value.practiceId, 0);
                                                                            handleTopic1Click();
                                                                            setTryingNo(0);
                                                                            if (isFirstTry || isRepTestFlg) {
                                                                                if(!practice || practice.practiceId !== value.practiceId && initCount.current !== 0 && completedQuestionBooks.includes(value.practiceId)) {
                                                                                    setIsTopicOpen(false);
                                                                                    setIsTopicOpen(false);
                                                                                    setIsParentClicked(false);
                                                                                } else if(!practice || practice.practiceId !== value.practiceId && initCount.current !== 0 && completedQuestionBooks.includes(value.practiceId)) {
                                                                                    setIsTopicOpen(true);
                                                                                    setIsTopicOpen(true);
                                                                                    setIsParentClicked(true);
                                                                                } else if (!practice || practice.practiceId !== value.practiceId && initCount.current === 0 && !completedQuestionBooks.includes(value.practiceId)) {
                                                                                    setIsTopicOpen(true);
                                                                                    setIsParentClicked(true);
                                                                                }
                                                                            } else {
                                                                                setIsTopicOpen(false);
                                                                                setIsParentClicked(true);
                                                                            }
                                                                            if(value.triedCount === 0) {
                                                                                if(!isCompleted && !isRepTestFlg && initCount.current !== 0 && isCompleted) {
                                                                                    setIsTopicOpen(false);
                                                                                    setIsParentClicked(false);
                                                                                } else if(!isCompleted && !isRepTestFlg && !isTopicOpen && completedQuestionBooks.includes(value.practiceId)) {    
                                                                                    setIsTopicOpen(false);
                                                                                    setIsParentClicked(false);
                                                                                } else if(!isCompleted && !isRepTestFlg && !isTopicOpen && !completedQuestionBooks.includes(value.practiceId) && initCount.current === 0) {    
                                                                                    setIsTopicOpen(true);
                                                                                    setIsParentClicked(true);
                                                                                } else if(!isCompleted && !isRepTestFlg && !isTopicOpen) {    
                                                                                    setIsTopicOpen(true);
                                                                                    setIsParentClicked(true);
                                                                                } else if(isCompletedRef.current && initCount.current !== 0) {
                                                                                    setIsTopicOpen(false);
                                                                                    setIsParentClicked(false);
                                                                                } else if(!isCompletedRef.current && isRepTestFlg) {
                                                                                    setIsTopicOpen(true);
                                                                                    setIsParentClicked(true);
                                                                                }
                                                                            } else {
                                                                                setIsRepTestFlg(false);
                                                                                setIsTopicOpen(false);
                                                                            }
                                                                        } else {
                                                                            if(isCompletedRef.current) {
                                                                                if(isRepTestFlg && value.triedCount > 0) {
                                                                                    if(isTopicOpen){
                                                                                        setIsTopicOpen(false);
                                                                                        setIsParentClicked(false);
                                                                                    } else {
                                                                                        setIsTopicOpen(false);
                                                                                        setIsParentClicked(false);
                                                                                    }
                                                                                }
                                                                                if(!isRepTestFlg && value.triedCount > 0) {
                                                                                    if(isTopicOpen){
                                                                                        setIsTopicOpen(false);
                                                                                        setIsParentClicked(false);
                                                                                    } else {
                                                                                        setIsTopicOpen(false);
                                                                                        setIsParentClicked(false);
                                                                                    }
                                                                                }
                                                                                if(isRepTestFlg && value.triedCount === 0 && isTopicOpen) {
                                                                                        setIsTopicOpen(false);
                                                                                        setIsParentClicked(false);
                                                                                }
                                                                                if(isRepTestFlg && value.triedCount === 0 && !isTopicOpen) {
                                                                                    setIsTopicOpen(false);
                                                                                    setIsParentClicked(false);
                                                                                }
                                                                                if(!isRepTestFlg && value.triedCount === 0 && isTopicOpen) {
                                                                                    setIsTopicOpen(false);
                                                                                    setIsParentClicked(false);
                                                                                }
                                                                            } else if(!isCompletedRef.current){
                                                                                if(!isTopicOpen) {
                                                                                    setIsTopicOpen(true);
                                                                                    setIsParentClicked(true);
                                                                                } else if(isTopicOpen) {
                                                                                    setIsTopicOpen(false);
                                                                                    setIsParentClicked(false);
                                                                                }
                                                                            }
                                                                        }
                                                                        if (!isParentClicked) return;
                                                                    }}
                                                                    
                                                                    dense
                                                                >
                                                                    <ListItemIcon>
                                                                        <Checkbox
                                                                            edge="start"
                                                                            disabled
                                                                            checked={
                                                                                completedQuestionBooks.includes(value.practiceId) || value.triedCount > 0
                                                                            }
                                                                            tabIndex={-1}
                                                                            disableRipple
                                                                            inputProps={{ 'aria-labelledby': labelId }}
                                                                        />
                                                                    </ListItemIcon>
                                                                    <ListItemText id={labelId} primary={value.practiceName} />
                                                                </ListItemButton>

                                                                {/* ネストされたトピックリスト */}
                                                                {isTopicOpen && practice && practice.practiceId === value.practiceId &&<>
                                                                    {value.topic1 && (
                                                                    <ListItem
                                                                        sx={{
                                                                            backgroundColor:
                                                                            initNo.current >= 0 && initNo.current <= 4
                                                                            ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                            : '#ddeeff', // それ以外は通常色
                                                                            display: "block"
                                                                        }}
                                                                    >
                                                                    <ListItemButton sx={{ pl: 4 }}
                                                                        onClick={() => {
                                                                            handleTopic1Click();
                                                                            setTryingNo(1);
                                                                        }}
                                                                        disabled={!isParentClicked}
                                                                    >
                                                                            <ListItemIcon>
                                                                                <Checkbox
                                                                                    edge="start"
                                                                                    disabled
                                                                                    checked={firstFiveAnswered}
                                                                                    tabIndex={-1}
                                                                                    disableRipple
                                                                                    inputProps={{ 'aria-labelledby': labelId }}
                                                                                />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic1} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic2 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 5 && initNo.current <= 9
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                         display: "block"
                                                                    }}>
                                                                    <ListItemButton sx={{ pl: 4 }}
                                                                        onClick={() => {
                                                                            handleTopic2Click();
                                                                            setTryingNo(5);
                                                                        }}
                                                                        disabled={!isParentClicked}
                                                                    >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={nextFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic2} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic3 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 10 && initNo.current <= 14
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic3Click();
                                                                                setTryingNo(10);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={thirdFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic3} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic4 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 15 && initNo.current <= 19
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic4Click();
                                                                                setTryingNo(15);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={fourthFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic4} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic5 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 20 && initNo.current <= 24
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic5Click();
                                                                                setTryingNo(20);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={fifthFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic5} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic6 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 25 && initNo.current <= 29
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic6Click();
                                                                                setTryingNo(25);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={sixthFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic6} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic7 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 30 && initNo.current <= 34
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic7Click();
                                                                                setTryingNo(30);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={seventhFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic7} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic8 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 35 && initNo.current <= 39
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic8Click();
                                                                                setTryingNo(35);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={eighthFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic8} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic9 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 40 && initNo.current <= 44
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic9Click();
                                                                                setTryingNo(40);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={ninthFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic9} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic10 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 45 && initNo.current <= 49
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic10Click();
                                                                                setTryingNo(45);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={tenthFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic10} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic11 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 41 && initNo.current <= 44
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic11Click();
                                                                                setTryingNo(50);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={eleventhFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic11} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic12 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 45 && initNo.current <= 49
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic12Click();
                                                                                setTryingNo(55);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={twelfthFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic12} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic13 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 50 && initNo.current <= 54
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic13Click();
                                                                                setTryingNo(60);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={thirteenthFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic13} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                {value.topic14 && (
                                                                    <ListItem sx={{ 
                                                                        backgroundColor:
                                                                        initNo.current >= 55 && initNo.current <= 59
                                                                        ? '#d9d9d9' // 問題1〜5に該当 → 赤背景
                                                                        : '#ddeeff', // それ以外は通常色
                                                                        display: "block"
                                                                    }}>
                                                                        <ListItemButton
                                                                            sx={{ pl: 4 }}
                                                                            onClick={() => {
                                                                                handleTopic14Click();
                                                                                setTryingNo(65);
                                                                            }}
                                                                            disabled={!isParentClicked}
                                                                        >
                                                                            <ListItemIcon>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                disabled
                                                                                checked={fourteenthFiveAnswered}
                                                                                tabIndex={-1}
                                                                                disableRipple
                                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                                            />
                                                                            </ListItemIcon>
                                                                            <ListItemText primary={value.topic14} />
                                                                        </ListItemButton>
                                                                    </ListItem>
                                                                )}
                                                                </>}
                                                            </ListItem>
                                                        )
                                                    })}
                                                </List>
                                            </Collapse>
                                        </>
                                    );
                                })}
                            </List>
                        </Box>
                    </Container>
                </Grid>}
                {(side === 'tests' && phase === 'test') &&  (innerWidth > breakWidth || isShowSide) &&
                <Grid item xs={3.7} lg={4}>
                    <Container maxWidth='md'>
                        <Box marginTop={2} maxWidth={380} bgcolor={'#dddddd'} padding={1}>
                            {practice?.practiceName}
                        </Box>
                        <Box sx={{overflowY: 'scroll', height: '470px'}}>
                            <List sx={{ width: '100%', maxWidth: 380, bgcolor: 'background.paper' }}>
                            {tests && tests?.map((value, idx) => {
                                const labelId = `checkbox-list-label-${value}`;

                                return (
                                <ListItem
                                    key={`t_${value.testId}`}
                                    disablePadding
                                    sx={{backgroundColor: '#f8f8f8'}}
                                >
                                    <ListItemButton
                                        role={undefined}
                                        onClick={()=>{ 
                                            const tmpAnswers: number[][] = [];
                                            for (let i = 0; i < tests?.length; i++) {
                                                if (i === tryingNo) {
                                                    tmpAnswers.push(selectedCoice);
                                                } else {
                                                    tmpAnswers.push(userAnswers[i])
                                                }
                                            } 
                                            setUserAnswers(tmpAnswers);
                                            setTryingNo(idx);
                                            initNo.current = idx;
                                        }}
                                        dense
                                    >
                                    <ListItemText
                                        id={labelId}
                                        primary={
                                            <Box>
                                                ☆問題{idx+1}　{userAnswers[idx]?.length === 0 && <Chip label='未回答'/>}
                                            </Box>
                                        }
                                        secondary={
                                            `　${value.question?.length > 20 
                                                ? value.question.substring(0,19) + '...'
                                                : value.question}`
                                        }
                                    />
                                    </ListItemButton>
                                </ListItem>
                                );
                            })}
                            </List>
                        </Box>
                    </Container>
                </Grid>}
                {innerWidth <= breakWidth && isShowSide &&
                    <Grid item xs={0.3} paddingTop={30} marginLeft={-2}>
                        <IconButton
                            color="inherit"
                            aria-label="close drawer"
                            edge="end"
                            onClick={() => setIsShowSide(false)}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Grid>
                }
            </Grid>
        </UserFrame>
    )
}

export default UsersProblemTemplate;